import { useState, useCallback, useMemo } from 'react';
import { getDateRange } from '../utils/dateUtils';

/**
 * Manages period-filter state (period preset, dateFrom, dateTo) and
 * exposes a `filterByPeriod(records, dateField)` helper for client-side filtering.
 *
 * When the user picks a preset, dateFrom/dateTo are computed automatically.
 * When the user manually edits a date, the period switches to "custom".
 */
export function usePeriodFilter(defaultPeriod = 'all_time') {
  const [period, setPeriod] = useState(defaultPeriod);
  const [dateFrom, setDateFrom] = useState(() => getDateRange(defaultPeriod).from);
  const [dateTo, setDateTo] = useState(() => getDateRange(defaultPeriod).to);

  const handlePeriodChange = useCallback((newPeriod) => {
    setPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      const range = getDateRange(newPeriod);
      setDateFrom(range.from);
      setDateTo(range.to);
    }
  }, []);

  const handleDateFromChange = useCallback((value) => {
    setDateFrom(value);
    setPeriod('custom');
  }, []);

  const handleDateToChange = useCallback((value) => {
    setDateTo(value);
    setPeriod('custom');
  }, []);

  const filterByPeriod = useCallback(
    (records, dateField) => {
      if (!dateFrom && !dateTo) return records;

      return records.filter((r) => {
        const raw = r[dateField];
        if (!raw) return false;
        const d = raw.substring(0, 10);
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
        return true;
      });
    },
    [dateFrom, dateTo],
  );

  return useMemo(
    () => ({
      period,
      dateFrom,
      dateTo,
      onPeriodChange: handlePeriodChange,
      onDateFromChange: handleDateFromChange,
      onDateToChange: handleDateToChange,
      filterByPeriod,
    }),
    [period, dateFrom, dateTo, handlePeriodChange, handleDateFromChange, handleDateToChange, filterByPeriod],
  );
}
